
import React, { useState } from 'react';
import { Plus, Trash2, Users, ChevronDown, ChevronUp, UserPlus, Home, Send, Loader2, Edit2 } from 'lucide-react';
import FileImporter from './FileImporter';
import { createCategory, addAthleteToCategory, deleteAthlete, deleteCategoryApi, updateCategoryApi, updateAthleteApi, updateAthleteSeedApi } from '../services/api';
import { translateError } from '../utils/errorTranslator';
import Swal from 'sweetalert2';

export default function CategoryManager({ competitionId, categories, onAddCategory, onDeleteCategory, onSelectCategory, loadInitialData, readOnly = false }) {
  const [newCatRows, setNewCatRows] = useState([{ name: "" }]);
  const [loading, setLoading] = useState(false);
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [newAthleteRows, setNewAthleteRows] = useState({}); // { catId: [{name, club}] }

  const toggleExpand = (catId) => {
    const newExpanded = new Set(expandedCats);
    if (newExpanded.has(catId)) newExpanded.delete(catId);
    else newExpanded.add(catId);
    setExpandedCats(newExpanded);
  };

  const handleAddCatRow = () => setNewCatRows([...newCatRows, { name: "" }]);
  const handleRemoveCatRow = (index) => {
    if (newCatRows.length === 1) return;
    setNewCatRows(newCatRows.filter((_, i) => i !== index));
  };
  const handleCatNameChange = (index, value) => {
    const newRows = [...newCatRows];
    newRows[index].name = value;
    setNewCatRows(newRows);
  };

  const handleBatchAddCats = async (e) => {
    e.preventDefault();
    if (readOnly || loading) return;

    const namesToAdd = newCatRows.map(r => r.name.trim()).filter(name => name !== "");
    if (namesToAdd.length === 0) return;

    const existingNames = categories.map(c => c.name.toLowerCase());
    if (namesToAdd.some(name => existingNames.includes(name.toLowerCase()))) {
        Swal.fire({ icon: 'error', title: 'Doublon', text: 'Une de ces catégories existe déjà.' });
        return;
    }

    setLoading(true);
    try {
      const promises = namesToAdd.map(name => createCategory({ name, competition_id: competitionId }));
      const newCats = await Promise.all(promises);
      onAddCategory(newCats);
      setNewCatRows([{ name: "" }]);
      Swal.fire({ icon: 'success', title: 'Catégories ajoutées', timer: 1500, showConfirmButton: false });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Échec de la création.' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAthleteRow = (catId) => {
    const current = newAthleteRows[catId] || [];
    setNewAthleteRows({ ...newAthleteRows, [catId]: [...current, { name: "", club: "" }] });
  };

  const handleRemoveAthleteRow = (catId, index) => {
    const current = newAthleteRows[catId] || [];
    setNewAthleteRows({ ...newAthleteRows, [catId]: current.filter((_, i) => i !== index) });
  };

  const handleAthleteChange = (catId, index, field, value) => {
    const current = [...(newAthleteRows[catId] || [])];
    current[index][field] = value;
    setNewAthleteRows({ ...newAthleteRows, [catId]: current });
  };

  const handleSaveAthletes = async (catId) => {
    const rows = newAthleteRows[catId] || [];
    const validRows = rows.filter(r => r.name.trim());
    if (validRows.length === 0) return;

    // Vérification des doublons locaux et existants
    const category = categories.find(c => c.id === catId);
    const existingAthletes = category?.athletes || [];
    
    const duplicates = [];
    const filteredRows = [];

    validRows.forEach(row => {
        const isDuplicate = existingAthletes.some(a => 
            (a.name || `${a.nom} ${a.prenom}`).toLowerCase().trim() === row.name.toLowerCase().trim() &&
            (a.club || "").toLowerCase().trim() === (row.club || "").toLowerCase().trim()
        );
        if (isDuplicate) {
            duplicates.push(row.name);
        } else {
            filteredRows.push(row);
        }
    });

    if (filteredRows.length === 0 && duplicates.length > 0) {
        Swal.fire({ icon: 'warning', title: 'Doublons', text: `Les athlètes suivants existent déjà dans cette catégorie : ${duplicates.join(', ')}` });
        return;
    }

    setLoading(true);
    try {
      const promises = filteredRows.map(row => addAthleteToCategory(catId, { name: row.name, club: row.club }, competitionId));
      await Promise.all(promises);
      setNewAthleteRows({ ...newAthleteRows, [catId]: [] });
      if (loadInitialData) await loadInitialData();
      
      if (duplicates.length > 0) {
          Swal.fire({ 
              icon: 'info', 
              title: 'Enregistrement partiel', 
              text: `${filteredRows.length} athlètes ajoutés. Les doublons suivants ont été ignorés : ${duplicates.join(', ')}` 
          });
      } else {
          Swal.fire({ icon: 'success', title: 'Athlètes enregistrés', timer: 1500, showConfirmButton: false });
      }
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Échec de l\'enregistrement.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = async (catId, currentName) => {
    const { value: newName } = await Swal.fire({
      title: 'Renommer la catégorie',
      input: 'text',
      inputValue: currentName,
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler',
      inputValidator: (value) => {
        if (!value) return 'Le nom ne peut pas être vide';
      }
    });

    if (newName && newName !== currentName) {
      try {
        setLoading(true);
        await updateCategoryApi(catId, newName);
        if (loadInitialData) await loadInitialData();
        Swal.fire({ icon: 'success', title: 'Catégorie renommée', timer: 1500 });
      } catch (err) {
        Swal.fire('Erreur', translateError(err), 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditAthlete = async (athlete) => {
    const { value: formValues } = await Swal.fire({
      title: 'Modifier l\'athlète',
      html:
        `<input id="swal-input1" class="swal2-input" placeholder="Nom Prénom" value="${athlete.name || `${athlete.nom} ${athlete.prenom}`}">` +
        `<input id="swal-input2" class="swal2-input" placeholder="Club" value="${athlete.club || ""}">`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler',
      preConfirm: () => {
        return {
          name: document.getElementById('swal-input1').value,
          club: document.getElementById('swal-input2').value
        }
      }
    });

    if (formValues) {
      try {
        setLoading(true);
        await updateAthleteApi(athlete.id, formValues);
        if (loadInitialData) await loadInitialData();
        Swal.fire({ icon: 'success', title: 'Athlète mis à jour', timer: 1500 });
      } catch (err) {
        Swal.fire('Erreur', translateError(err), 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSetSeed = async (athlete) => {
    const currentSeed = athlete.seed ?? '';
    const { value } = await Swal.fire({
      title: 'Classement de l\'athlète',
      input: 'select',
      inputOptions: {
        '': 'Aucun classement',
        '1': '🥇 Tête de série n°1',
        '2': '🥈 Tête de série n°2',
      },
      inputValue: String(currentSeed),
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler'
    });

    if (value !== undefined) {
      try {
        await updateAthleteSeedApi(athlete.id, value === '' ? null : parseInt(value));
        if (loadInitialData) await loadInitialData();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de mettre à jour le classement.' });
      }
    }
  };

  const handleDeleteAthlete = async (catId, athleteId) => {
    const result = await Swal.fire({
        title: 'Supprimer l\'athlète ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e53e3e',
        confirmButtonText: 'Supprimer'
    });

    if (result.isConfirmed) {
        try {
            await deleteAthlete(catId, athleteId);
            if (loadInitialData) await loadInitialData();
            Swal.fire('Supprimé !', '', 'success');
        } catch (err) {
            Swal.fire('Erreur', 'Échec de la suppression', 'error');
        }
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (readOnly) return;
    
    const result = await Swal.fire({
      title: 'Supprimer la catégorie ?',
      text: "Cela supprimera définitivement tous les athlètes et matchs associés de cette catégorie !",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      confirmButtonText: 'Supprimer définitivement'
    });

    if (result.isConfirmed) {
      try {
        Swal.showLoading();
        await deleteCategoryApi(id);
        onDeleteCategory(id);
        Swal.fire('Supprimé !', 'La catégorie a été retirée.', 'success');
      } catch (error) {
        console.error(error);
        Swal.fire({
            icon: 'error',
            title: 'Échec de la suppression',
            text: translateError(error)
        });
      }
    }
  };

  const handleDeleteAllCategories = async () => {
    if (readOnly || categories.length === 0) return;

    const result = await Swal.fire({
        title: 'TOUT SUPPRIMER ?',
        text: `Vous allez supprimer les ${categories.length} catégories ainsi que TOUS les athlètes et matchs associés. Cette action est irréversible !`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e53e3e',
        confirmButtonText: 'Oui, tout supprimer',
        cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
        try {
            setLoading(true);
            Swal.fire({ title: 'Suppression en cours...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            
            // On supprime chaque catégorie une par une via l'API
            for (const cat of categories) {
                await deleteCategoryApi(cat.id);
            }

            if (loadInitialData) await loadInitialData();
            Swal.fire('Supprimé !', 'Toutes les catégories ont été supprimées.', 'success');
        } catch (error) {
            console.error(error);
            Swal.fire('Erreur', 'Une erreur est survenue lors de la suppression groupée.', 'error');
        } finally {
            setLoading(false);
        }
    }
  };

  const totalAthletes = categories.reduce((sum, cat) => sum + (cat.athletes?.length || 0), 0);

  return (
    <div style={styles.container}>
      {/* RÉSUMÉ */}
      <div style={{ display: 'flex', gap: '15px' }}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{categories.length}</div>
          <div style={styles.statLabel}>Catégories</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{totalAthletes}</div>
          <div style={styles.statLabel}>Athlètes</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {categories.length > 0 ? (totalAthletes / categories.length).toFixed(1) : '—'}
          </div>
          <div style={styles.statLabel}>Moy. / catégorie</div>
        </div>
      </div>

      {/* SECTION AJOUT CATÉGORIES */}
      {!readOnly && (
        <div style={styles.card}>
          <div style={{ ...styles.header, justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Users size={20} color="#3182ce" />
                <h3 style={styles.title}>Créer de nouvelles catégories</h3>
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {categories.length > 0 && (
                    <button
                        onClick={handleDeleteAllCategories}
                        style={{ ...styles.submitBtn, backgroundColor: '#e53e3e', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <Trash2 size={18} /> Tout supprimer
                    </button>
                )}
                <FileImporter
                    competitionId={competitionId}
                    existingCategories={categories}
                    onImportSuccess={loadInitialData}
                />
            </div>
          </div>
          <form onSubmit={handleBatchAddCats}>
            <div style={styles.rowsContainer}>
              {newCatRows.map((row, index) => (
                <div key={index} style={styles.row}>
                  <input
                    style={styles.input}
                    placeholder="Nom (ex: M80...)"
                    value={row.name}
                    onChange={(e) => handleCatNameChange(index, e.target.value)}
                  />
                  <button type="button" onClick={() => handleRemoveCatRow(index)} style={styles.removeBtn} disabled={newCatRows.length === 1}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div style={styles.footer}>
              <button type="button" onClick={handleAddCatRow} style={styles.addRowBtn}><Plus size={16} /> Ligne</button>
              <button type="submit" disabled={loading} style={styles.submitBtn}>Enregistrer les catégories</button>
            </div>
          </form>
        </div>
      )}

      {/* LISTE DES CATÉGORIES ET LEURS ATHLÈTES */}
      <div style={styles.grid}>
        {categories.map((cat) => (
          <div key={cat.id} style={styles.catSection}>
            <div style={styles.catHeader} onClick={() => toggleExpand(cat.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={styles.iconCircle}><Users size={20} color="#3182ce" /></div>
                <div>
                  <div style={styles.catName}>{cat.name}</div>
                  <div style={styles.catStats}>{cat.athletes?.length || 0} tireurs</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {!readOnly && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={(e) => { e.stopPropagation(); handleEditCategory(cat.id, cat.name); }} style={styles.editBtn}><Edit2 size={18} /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDelete(e, cat.id); }} style={styles.deleteBtn}><Trash2 size={18} /></button>
                    </div>
                )}
                {expandedCats.has(cat.id) ? <ChevronUp size={24} color="#a0aec0" /> : <ChevronDown size={24} color="#a0aec0" />}
              </div>
            </div>

            {expandedCats.has(cat.id) && (
              <div style={styles.catContent}>
                {/* Liste des athlètes existants */}
                <div style={styles.athleteList}>
                  {cat.athletes?.length > 0 ? (
                    cat.athletes.map(athlete => (
                      <div key={athlete.id} style={styles.athleteItem}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {athlete.seed === 1 && (
                            <span title="Tête de série n°1" style={styles.seedBadge1}>#1</span>
                          )}
                          {athlete.seed === 2 && (
                            <span title="Tête de série n°2" style={styles.seedBadge2}>#2</span>
                          )}
                          <span style={{ fontWeight: 'bold' }}>{athlete.name || `${athlete.nom} ${athlete.prenom}`}</span>
                          <span style={{ color: '#718096', fontSize: '13px' }}>{athlete.club}</span>
                        </div>
                        {!readOnly && (
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => handleSetSeed(athlete)} style={styles.seedBtn} title="Définir le classement">
                                  {athlete.seed ? '★' : '☆'}
                                </button>
                                <button onClick={() => handleEditAthlete(athlete)} style={styles.editBtnSmall}><Edit2 size={14} /></button>
                                <button onClick={() => handleDeleteAthlete(cat.id, athlete.id)} style={styles.removeBtn}><Trash2 size={14} /></button>
                            </div>
                        )}
                      </div>
                    ))
                  ) : <p style={{ color: '#a0aec0', fontSize: '13px', padding: '10px' }}>Aucun athlète dans cette catégorie.</p>}
                </div>

                {/* Saisie de nouveaux athlètes */}
                {!readOnly && (
                  <div style={styles.addAthleteSection}>
                    <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Ajouter des tireurs</h4>
                    { (newAthleteRows[cat.id] || []).map((row, index) => (
                      <div key={index} style={{ ...styles.row, marginBottom: '10px' }}>
                        <input style={styles.input} placeholder="Nom Prénom" value={row.name} onChange={(e) => handleAthleteChange(cat.id, index, 'name', e.target.value)} />
                        <input style={styles.input} placeholder="Club" value={row.club} onChange={(e) => handleAthleteChange(cat.id, index, 'club', e.target.value)} />
                        <button onClick={() => handleRemoveAthleteRow(cat.id, index)} style={styles.removeBtn}><Trash2 size={16} /></button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                      <button onClick={() => handleAddAthleteRow(cat.id)} style={styles.addRowBtn}><Plus size={14} /> Tireur</button>
                      {(newAthleteRows[cat.id]?.length > 0) && (
                        <button onClick={() => handleSaveAthletes(cat.id)} disabled={loading} style={styles.submitBtnSmall}>Enregistrer</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', gap: '25px' },
  card: { backgroundColor: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' },
  header: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #edf2f7', paddingBottom: '15px' },
  title: { margin: 0, fontSize: '18px', color: '#2d3748', fontWeight: '600' },
  rowsContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' },
  row: { display: 'flex', gap: '10px', alignItems: 'center' },
  input: { flex: 1, padding: '10px 15px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' },
  removeBtn: { background: 'none', border: 'none', color: '#feb2b2', cursor: 'pointer', padding: '5px' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #edf2f7', paddingTop: '20px' },
  addRowBtn: { display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px dashed #3182ce', color: '#3182ce', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  submitBtn: { backgroundColor: '#38a169', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' },
  submitBtnSmall: { backgroundColor: '#3182ce', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '15px', alignItems: 'start' },
  catSection: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' },
  catHeader: { padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: '#fff', borderBottom: '1px solid #f7fafc' },
  catContent: { padding: '20px', backgroundColor: '#fcfcfc', borderTop: '1px solid #edf2f7' },
  iconCircle: { width: '40px', height: '40px', backgroundColor: '#ebf8ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  catName: { fontWeight: 'bold', color: '#2d3748' },
  catStats: { fontSize: '12px', color: '#718096' },
  athleteList: { marginBottom: '20px' },
  athleteItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #edf2f7', backgroundColor: 'white', borderRadius: '4px', marginBottom: '5px' },
  addAthleteSection: { padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' },
  deleteBtn: { background: 'none', border: 'none', color: '#feb2b2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  editBtn: { background: 'none', border: 'none', color: '#3182ce', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  editBtnSmall: { background: 'none', border: 'none', color: '#a0aec0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  seedBtn: { background: 'none', border: 'none', color: '#d4a017', cursor: 'pointer', fontSize: '15px', padding: '2px 4px', lineHeight: 1 },
  seedBadge1: { fontSize: '11px', fontWeight: '800', color: '#744210', backgroundColor: '#f6e05e', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 },
  seedBadge2: { fontSize: '11px', fontWeight: '800', color: '#4a5568', backgroundColor: '#e2e8f0', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 },
  statCard: { flex: 1, backgroundColor: 'white', borderRadius: '12px', padding: '16px 20px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
  statValue: { fontSize: '28px', fontWeight: '800', color: '#2c5282' },
  statLabel: { fontSize: '12px', color: '#718096', marginTop: '4px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }
};
